import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { AxiosError } from "axios";
import { useAuthStore } from "@/app/store/auth.store";
import { useGetMe } from "@/modules/auth/hooks/useGetMe";
import { Roles } from "@fysaro/domain";
import { useTranslation } from "react-i18next";
import { FRONTEND_ROUTES } from "@fysaro/shared";
import { FullScreenLoader } from "@/shared/ui/components/FullScreenLoader";
import { MaintenancePage } from "@/modules/auth/ui/MaintenancePage";
import { useEffect } from "react";

function getApiErrorCode(error: unknown): string | undefined {
  if (error == null) return undefined;

  const axiosError = error as AxiosError<{ error?: { code?: string } | null }>;
  return axiosError.response?.data?.error?.code ?? undefined;
}

function shouldShowMaintenancePage(
  profile: ReturnType<typeof useGetMe>["data"],
  error: unknown,
  role: string | undefined,
): boolean {
  if (role === Roles.SUPERADMIN) return false;

  if (getApiErrorCode(error) === "SYSTEM.MAINTENANCE") return true;

  return !!profile?.settings?.flags?.maintenanceMode;
}

export function AppGate() {
  const { t, i18n } = useTranslation(["auth"]);
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((u) => u.user);

  const role = user?.role;
  const path = location.pathname;

  // Perfil remoto (solo si hay token activo tras el arranque).
  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    error: profileError,
  } = useGetMe({
    enabled: !!token,
  });

  // 🌟 EL TRUCO: Sincronización proactiva del idioma del perfil del usuario
  useEffect(() => {
    const userLanguage = profile?.language || user?.language;
    
    if (userLanguage && i18n.language !== userLanguage) {
      i18n.changeLanguage(userLanguage);
      // Forzamos la escritura para la siguiente recarga del navegador
      localStorage.setItem("i18nextLng", userLanguage); 
    }
  }, [profile?.language, user?.language, i18n]);

  // 3. Intento de acceso privado SIN Token -> Redirección inteligente
  if (!token) {
    const currentPath = encodeURIComponent(location.pathname + location.search);
    let redirect = FRONTEND_ROUTES.auth.loginClinic;

    if (role === Roles.PATIENT || path.startsWith("/patient") || path.startsWith("/p/")) {
      redirect = `${FRONTEND_ROUTES.auth.loginPatient}?returnTo=${currentPath}`;
    } else if (role === Roles.SUPERADMIN || path.startsWith("/superadmin")) {
      redirect = FRONTEND_ROUTES.auth.loginSuperadmin;
    }

    return <Navigate to={redirect} replace state={{ from: location }} />;
  }

  // 4. Mantenimiento o carga de perfil
  if (
    shouldShowMaintenancePage(profile, profileError, profile?.role ?? role)
  ) {
    return <MaintenancePage />;
  }

  if (isProfileLoading) {
    return <FullScreenLoader label={t("auth:app_gate_loading")} />;
  }

  if (isProfileError || !profile) {
    const loginPath =
      role === Roles.PATIENT
        ? FRONTEND_ROUTES.auth.loginPatient
        : FRONTEND_ROUTES.auth.loginClinic;

    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  // ==========================================
  // 5. Matriz de Autorización (Tu lógica impecable)
  // ==========================================
  
  // SUPERADMIN
  if (profile.role === Roles.SUPERADMIN) {
    if (path.startsWith("/superadmin")) return <Outlet />;
    return <Navigate to={FRONTEND_ROUTES.superadmin.dashboard} replace />;
  }

  // OWNER & ONBOARDING
  const isAtOnboarding = path.startsWith("/onboarding");
  const { isCompleted } = profile.onboarding;

  if (profile.role === Roles.OWNER) {
    if (!isCompleted) {
      if (!isAtOnboarding) {
        const targetStep = profile.onboarding.currentStep || 1;
        return <Navigate to={`/onboarding/step-${targetStep}`} replace />;
      }
      return <Outlet />;
    }

    if (isAtOnboarding && isCompleted) {
      return <Navigate to={FRONTEND_ROUTES.tenant.dashboard} replace />;
    }
  }

  // PATIENT
  if (profile.role === Roles.PATIENT) {
    if (path.startsWith("/patient")) return <Outlet />;
    return <Navigate to={FRONTEND_ROUTES.patient.selectClinic} replace />;
  }

  // TENANT GENERAL (Owner ya validado o Staff)
  if (!path.startsWith("/tenant") && !isAtOnboarding) {
    return <Navigate to={FRONTEND_ROUTES.tenant.dashboard} replace />;
  }

  return <Outlet />;
}
